import { useParams, Link } from "react-router-dom";
import { getCategoryBySlug } from "../content/marketplace-content-loader";

/* ────── Utility Icons ────── */

function StarIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="#C4993C" stroke="none">
      <path d="M6 .8l1.5 3 3.3.5-2.4 2.3.6 3.4L6 8.2 3 10l.6-3.4L1.2 4.3l3.3-.5z" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 2v6M3.5 5.5L6 8l2.5-2.5" />
      <path d="M2.5 10h7" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 3L5 8l5 5" />
    </svg>
  );
}

/* ────── Page Component ────── */

export function MarketplaceCategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;

  if (!category) {
    return (
      <div className="px-6 py-20 text-center">
        <h1 className="font-serif text-3xl font-bold text-kin-black">Category not found</h1>
        <p className="mt-3 text-kin-body/80">
          The category you&rsquo;re looking for doesn&rsquo;t exist.
        </p>
        <Link
          to="/marketplace"
          className="mt-6 inline-block text-sm font-medium text-kin-green-dark underline underline-offset-2"
        >
          Back to Marketplace
        </Link>
      </div>
    );
  }

  return (
    <div className="px-6 pb-16 pt-6 sm:px-8 sm:pb-20 sm:pt-10">
      <div className="mx-auto max-w-6xl">
        {/* Breadcrumb */}
        <nav>
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-kin-muted transition hover:text-kin-black"
          >
            <span className="inline-block h-4 w-4">
              <ChevronLeftIcon />
            </span>
            Marketplace
          </Link>
        </nav>

        {/* Hero */}
        <section className="mt-8 text-center">
          <span
            className="inline-flex h-16 w-16 items-center justify-center rounded-3xl text-2xl"
            style={{ background: category.color }}
          >
            {category.icon}
          </span>
          <h1 className="mt-4 font-serif text-4xl font-bold text-kin-black sm:text-5xl">
            {category.name}
            <span className="text-kin-green-dark">.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-kin-body/80">
            {category.desc}
          </p>
          <p className="mt-2 text-sm text-kin-muted">{category.count} guides available</p>
        </section>

        {/* Guides Grid */}
        <section className="mt-12">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {category.guides.map((guide) => (
              <Link
                key={guide.name}
                to={`/marketplace/${guide.slug}`}
                className="kin-surface group rounded-3xl p-5 transition hover:-translate-y-0.5"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{ background: category.color }}
                  />
                  <div>
                    <h3 className="font-semibold text-kin-black transition group-hover:text-kin-green-dark">
                      {guide.name}
                    </h3>
                    <p className="text-xs text-kin-muted">{guide.author}</p>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-kin-body/80">{guide.desc}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex gap-3">
                    <span className="inline-flex items-center gap-1 text-xs text-kin-muted">
                      <span className="inline-block h-3 w-3">
                        <StarIcon />
                      </span>
                      {guide.rating}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-kin-muted">
                      <span className="inline-block h-3 w-3">
                        <DownloadIcon />
                      </span>
                      {guide.installs}
                    </span>
                  </div>
                  <span className="text-xs font-semibold text-kin-green-dark">View →</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Back CTA */}
        <section className="mt-12 text-center">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-2 rounded-full border border-kin-olive/20 bg-kin-white/60 px-6 py-2.5 text-sm font-medium text-kin-body transition hover:bg-kin-white/90"
          >
            Browse all categories
          </Link>
        </section>
      </div>
    </div>
  );
}
